import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import i18n from "i18next";
import { LuMusic, LuSearch, LuPlay, LuPause, LuPlus, LuCheck } from "react-icons/lu";
import { fetchIdealStudioFiles } from "../../../common/server_utils.es6";

const LibrarySoundTab = forwardRef(
  (
    {
      onAddSound,
      onSelectionChange,
      allSoundLists = {},
      navItemsIds = [],
      navItemSelected,
      soundList = [],
      initialSoundIndex,
      initialStartTime = 0,
    },
    ref
  ) => {
    const getScenesUsingSound = (fileUrl) => {
      if (!fileUrl || !navItemsIds.length) return null;
      const totalScenes = navItemsIds.length;
      const scenesUsed = [];

      navItemsIds.forEach((navId, index) => {
        const sounds = allSoundLists[navId] || [];
        const isUsed = sounds.some((s) => {
          // file URL matches exactly or just the filename part matches
          if (!s.file) return false;
          return (
            s.file === fileUrl ||
            s.file.split("/").pop().split("?")[0] === fileUrl.split("/").pop().split("?")[0]
          );
        });
        if (isUsed) {
          scenesUsed.push(`${index + 1}/${totalScenes}`);
        }
      });

      if (scenesUsed.length === 0) return null;
      return scenesUsed.join(", ");
    };

    const getFirstUsedSceneIndex = (fileUrl) => {
      if (!fileUrl || !navItemsIds.length) return Infinity;
      for (let i = 0; i < navItemsIds.length; i++) {
        const navId = navItemsIds[i];
        const sounds = allSoundLists[navId] || [];
        const isUsed = sounds.some((s) => {
          if (!s.file) return false;
          return (
            s.file === fileUrl ||
            s.file.split("/").pop().split("?")[0] === fileUrl.split("/").pop().split("?")[0]
          );
        });
        if (isUsed) return i;
      }
      return Infinity;
    };
    const [files, setFiles] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [lastSelectedIdx, setLastSelectedIdx] = useState(null);
    const [hoveredId, setHoveredId] = useState(null);
    const [addedFileIds, setAddedFileIds] = useState([]);
    const [notification, setNotification] = useState("");

    const [playingId, setPlayingId] = useState(null);
    const audioRef = useRef(null);

    const [startTime, setStartTime] = useState(initialStartTime || 0);
    const [volume, setVolume] = useState(100);
    const [initialSelectionDone, setInitialSelectionDone] = useState(false);

    useEffect(() => {
      let isCancelled = false;

      const loadFiles = async () => {
        setLoading(true);
        setError("");
        try {
          const response = await fetchIdealStudioFiles({
            search: searchTerm || undefined,
            limit: 50,
            offset: 0,
            fileType: ["audio", "clone-voice"],
          });

          if (!isCancelled && response && Array.isArray(response.files)) {
            const mapped = response.files.map((file, idx) => ({
              id: file.id,
              name: file.fileName || file.filename || "Untitled",
              url: file.fileVirtualPath,
              raw: file,
              originalIndex: idx,
            }));

            mapped.sort((a, b) => {
              const aFirstScene = getFirstUsedSceneIndex(a.url);
              const bFirstScene = getFirstUsedSceneIndex(b.url);
              if (aFirstScene !== bFirstScene) {
                return aFirstScene - bFirstScene;
              }
              return a.originalIndex - b.originalIndex;
            });

            setFiles(mapped);
          }
        } catch (err) {
          if (!isCancelled) {
            setError(i18n.t("files_load_error") || "Sesler yüklenemedi");
            setFiles([]);
          }
        } finally {
          if (!isCancelled) {
            setLoading(false);
          }
        }
      };

      const timeoutId = setTimeout(loadFiles, 250);

      return () => {
        isCancelled = true;
        clearTimeout(timeoutId);
      };
    }, [searchTerm]);

    const handlePlayPause = (e, file) => {
      e.stopPropagation();

      if (playingId === file.id) {
        if (audioRef.current) {
          audioRef.current.pause();
        }
        setPlayingId(null);
      } else {
        if (audioRef.current) {
          audioRef.current.pause();
        }
        const audio = new Audio(file.url);
        audio.onended = () => {
          setPlayingId(null);
        };
        audio.play().catch((err) => console.error("Audio play error:", err));
        audioRef.current = audio;
        setPlayingId(file.id);
      }
    };

    const handleSelect = (e, fileId, index) => {
      if (e.shiftKey && lastSelectedIdx !== null && selectedIds.length > 0) {
        const start = Math.min(index, lastSelectedIdx);
        const end = Math.max(index, lastSelectedIdx);
        const rangeIds = files.slice(start, end + 1).map((f) => f.id);
        const newSelection = new Set([...selectedIds, ...rangeIds]);
        setSelectedIds(Array.from(newSelection));
      } else if (e.ctrlKey || e.metaKey) {
        if (selectedIds.includes(fileId)) {
          setSelectedIds(selectedIds.filter((id) => id !== fileId));
        } else {
          setSelectedIds([...selectedIds, fileId]);
        }
        setLastSelectedIdx(index);
      } else {
        setSelectedIds([fileId]);
        setLastSelectedIdx(index);
      }
    };

    useEffect(() => {
      if (onSelectionChange) {
        onSelectionChange(selectedIds.length);
      }
    }, [selectedIds, onSelectionChange]);

    useEffect(() => {
      if (!initialSelectionDone && files.length > 0) {
        if (typeof initialSoundIndex === "number" && soundList[initialSoundIndex]) {
          const targetUrl = soundList[initialSoundIndex].file;
          if (targetUrl) {
            const fileMatch = files.find((f) => {
              if (!f.url) return false;
              return (
                f.url === targetUrl ||
                f.url.split("/").pop().split("?")[0] === targetUrl.split("/").pop().split("?")[0]
              );
            });
            if (fileMatch) {
              setSelectedIds([fileMatch.id]);
            }
          }
        }
        setInitialSelectionDone(true);
      }
    }, [files, initialSoundIndex, soundList, initialSelectionDone]);

    useEffect(() => {
      if (selectedIds.length === 1 && navItemSelected) {
        const selectedFile = files.find((f) => f.id === selectedIds[0]);
        if (selectedFile && selectedFile.url) {
          const sceneSounds = allSoundLists[navItemSelected] || [];
          const existingSound = sceneSounds.find((s) => {
            if (!s.file) return false;
            return (
              s.file === selectedFile.url ||
              s.file.split("/").pop().split("?")[0] ===
                selectedFile.url.split("/").pop().split("?")[0]
            );
          });

          if (existingSound) {
            setStartTime(existingSound.startTime || 0);
            setVolume(
              existingSound.volumeLevel !== undefined
                ? Math.round(existingSound.volumeLevel * 100)
                : 100
            );
          } else {
            setStartTime(initialStartTime || 0);
            setVolume(100);
          }
        }
      } else if (selectedIds.length === 0) {
        setStartTime(initialStartTime || 0);
        setVolume(100);
      }
    }, [selectedIds, files, navItemSelected, allSoundLists, initialStartTime]);

    useImperativeHandle(
      ref,
      () => ({
        handleAddSelected: async () => {
          if (onAddSound && selectedIds.length > 0) {
            const selectedFiles = files.filter((f) => selectedIds.includes(f.id));
            for (let i = 0; i < selectedFiles.length; i++) {
              const f = selectedFiles[i];
              const isLast = i === selectedFiles.length - 1;
              if (f.url) {
                const duration = await new Promise((resolve) => {
                  const a = new Audio(f.url);
                  a.onloadedmetadata = () => resolve(a.duration);
                  a.onerror = () => resolve(0);
                });
                onAddSound(
                  {
                    file: f.url,
                    name: f.name,
                    startTime: Number(startTime) || 0,
                    endTime: (Number(startTime) || 0) + duration,
                    volumeLevel: volume / 100,
                  },
                  isLast
                );
              }
            }
          }
        },
      }),
      [selectedIds, files, onAddSound, startTime, volume]
    );

    useEffect(() => {
      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
        }
      };
    }, []);

    return (
      <div
        className="add-sound-modal__library"
        style={{ display: "flex", flexDirection: "column", gap: "16px", height: "100%" }}
      >
        <div className="add-sound-modal__library-header">
          <div
            className="add-image-modal__library-search"
            style={{ position: "relative", display: "flex", alignItems: "center" }}
          >
            <LuSearch
              className="add-image-modal__library-search-icon"
              style={{ position: "absolute", left: "12px", color: "#9ca3af" }}
            />
            <input
              type="text"
              placeholder={i18n.t("search") || "Arama"}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              style={{
                width: "100%",
                padding: "10px 10px 10px 36px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
              }}
            />
          </div>
        </div>

        {error && !loading && (
          <div
            className="add-image-modal__library-status"
            style={{ textAlign: "center", color: "#ef4444", padding: "20px" }}
          >
            {error}
          </div>
        )}

        {loading && (
          <div
            className="add-image-modal__library-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
              gap: "16px",
            }}
          >
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="add-image-modal__library-card add-image-modal__library-skeleton"
              >
                <div
                  className="add-image-modal__library-thumb add-image-modal__skeleton-thumb"
                  style={{ backgroundColor: "#f3f4f6", height: "100px", borderRadius: "8px" }}
                />
                <div
                  className="add-image-modal__library-name add-image-modal__skeleton-name"
                  style={{
                    backgroundColor: "#f3f4f6",
                    height: "16px",
                    marginTop: "8px",
                    borderRadius: "4px",
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {!loading && !error && (
          <div
            className="add-image-modal__library-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
              gap: "16px",
              overflowY: "auto",
              maxHeight: "360px",
              alignContent: "start",
              paddingBottom: "16px",
              paddingRight: "4px",
            }}
          >
            {files.map((file, index) => (
              <div
                key={file.id}
                className={`add-image-modal__library-card${
                  selectedIds.includes(file.id) ? " add-image-modal__library-card--active" : ""
                }`}
                style={{
                  position: "relative",
                  cursor: "pointer",
                  border: selectedIds.includes(file.id) ? "2px solid #7c3aed" : "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "8px",
                  transition: "all 0.2s",
                  backgroundColor: "#fff",
                }}
                onClick={(e) => handleSelect(e, file.id, index)}
                onMouseEnter={() => setHoveredId(file.id)}
                onMouseLeave={() =>
                  setHoveredId((current) => (current === file.id ? null : current))
                }
                onDoubleClick={() => {
                  if (onAddSound && file.url) {
                    const audio = new Audio(file.url);
                    audio.onloadedmetadata = () => {
                      onAddSound(
                        {
                          file: file.url,
                          name: file.name,
                          startTime: startTime,
                          endTime: startTime + audio.duration,
                          volumeLevel: 1,
                        },
                        false
                      );
                      setAddedFileIds((prev) => [...prev, file.id]);
                      setTimeout(() => {
                        setAddedFileIds((prev) => prev.filter((id) => id !== file.id));
                      }, 1000);
                      setNotification(i18n.t("Sound.FileAdded") || "Ses sahneye eklendi");
                      setTimeout(() => setNotification(""), 3000);
                    };
                    audio.onerror = () => {
                      onAddSound(
                        {
                          file: file.url,
                          name: file.name,
                          startTime: startTime,
                          endTime: startTime,
                          volumeLevel: 1,
                        },
                        false
                      );
                      setAddedFileIds((prev) => [...prev, file.id]);
                      setTimeout(() => {
                        setAddedFileIds((prev) => prev.filter((id) => id !== file.id));
                      }, 1000);
                      setNotification(i18n.t("Sound.FileAdded") || "Ses sahneye eklendi");
                      setTimeout(() => setNotification(""), 3000);
                    };
                  }
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 6,
                    display: "flex",
                    gap: 4,
                    zIndex: 5,
                    opacity:
                      hoveredId === file.id ||
                      playingId === file.id ||
                      addedFileIds.includes(file.id)
                        ? 1
                        : 0,
                    pointerEvents:
                      hoveredId === file.id ||
                      playingId === file.id ||
                      addedFileIds.includes(file.id)
                        ? "auto"
                        : "none",
                    transition: "opacity 150ms ease-out",
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => handlePlayPause(e, file)}
                    title={
                      playingId === file.id
                        ? i18n.t("Pause") || "Duraklat"
                        : i18n.t("Play") || "Oynat"
                    }
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 999,
                      border: "1px solid #e5e7eb",
                      backgroundColor: "rgba(255,255,255,0.95)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "#4b5563",
                    }}
                  >
                    {playingId === file.id ? <LuPause size={14} /> : <LuPlay size={14} />}
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (addedFileIds.includes(file.id)) return;
                      if (onAddSound && file.url) {
                        const audio = new Audio(file.url);
                        audio.onloadedmetadata = () => {
                          onAddSound(
                            {
                              file: file.url,
                              name: file.name,
                              startTime: startTime,
                              endTime: startTime + audio.duration,
                              volumeLevel: 1,
                            },
                            false
                          );
                          setAddedFileIds((prev) => [...prev, file.id]);
                          setTimeout(() => {
                            setAddedFileIds((prev) => prev.filter((id) => id !== file.id));
                          }, 1000);
                          setNotification(i18n.t("Sound.FileAdded") || "Ses sahneye eklendi");
                          setTimeout(() => setNotification(""), 3000);
                        };
                        audio.onerror = () => {
                          onAddSound(
                            {
                              file: file.url,
                              name: file.name,
                              startTime: startTime,
                              endTime: startTime,
                              volumeLevel: 1,
                            },
                            false
                          );
                          setAddedFileIds((prev) => [...prev, file.id]);
                          setTimeout(() => {
                            setAddedFileIds((prev) => prev.filter((id) => id !== file.id));
                          }, 1000);
                          setNotification(i18n.t("Sound.FileAdded") || "Ses sahneye eklendi");
                          setTimeout(() => setNotification(""), 3000);
                        };
                      }
                    }}
                    title={
                      addedFileIds.includes(file.id)
                        ? i18n.t("Added") || "Eklendi"
                        : i18n.t("AddToScene") || "Sahneye ekle"
                    }
                    disabled={addedFileIds.includes(file.id)}
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 999,
                      border: addedFileIds.includes(file.id)
                        ? "1px solid #10b981"
                        : "1px solid #e5e7eb",
                      backgroundColor: addedFileIds.includes(file.id)
                        ? "#ecfdf5"
                        : "rgba(255,255,255,0.95)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: addedFileIds.includes(file.id) ? "default" : "pointer",
                      color: addedFileIds.includes(file.id) ? "#10b981" : "#4b5563",
                      transform: addedFileIds.includes(file.id) ? "scale(1.05)" : "scale(1)",
                      transition: "all 0.2s ease-out",
                    }}
                  >
                    {addedFileIds.includes(file.id) ? <LuCheck size={14} /> : <LuPlus size={14} />}
                  </button>
                </div>

                <div
                  className="add-image-modal__library-thumb"
                  style={{
                    height: "100px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <LuMusic size={32} color="#94a3b8" />
                </div>
                <div
                  className="add-image-modal__library-name"
                  style={{
                    fontSize: "11px",
                    color: "#9ca3af",
                    fontWeight: 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={file.name}
                >
                  {file.name}
                </div>

                {(() => {
                  const usedIn = getScenesUsingSound(file.url);
                  if (usedIn) {
                    return (
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#059669",
                          marginTop: "4px",
                          fontWeight: 600,
                          backgroundColor: "#d1fae5",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          display: "inline-block",
                          width: "max-content",
                          position: "absolute",
                          top: "8px",
                          left: "12px",
                        }}
                        title={`Bu ses kullanılıyor: ${usedIn}`}
                      >
                        {usedIn}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ))}
            {files.length === 0 && !loading && !error && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "40px 20px",
                  color: "#9ca3af",
                }}
              >
                {i18n.t("FromLibraryComingSoon") || "Kütüphanede ses bulunamadı."}
              </div>
            )}
          </div>
        )}

        {/* Settings Block */}
        {!loading && !error && files.length > 0 && selectedIds.length > 0 && (
          <div
            className="add-sound-modal__upload-settings"
            style={{ marginTop: "auto", flexShrink: 0 }}
          >
            <div className="add-sound-modal__upload-settings-title">
              {i18n.t("AddSoundUpload.SoundSettings") || "Ses Ayarları"}
            </div>
            <div className="add-sound-modal__upload-settings-grid">
              <div className="add-sound-modal__upload-settings-field">
                <label>{i18n.t("AddSoundUpload.StartTime") || "Başlangıç Zamanı"}</label>
                <input
                  type="number"
                  value={startTime}
                  onChange={(event) => setStartTime(Number(event.target.value) || 0)}
                />
              </div>
              <div className="add-sound-modal__upload-settings-field">
                <div className="add-sound-modal__upload-settings-row">
                  <label>{i18n.t("AddSoundUpload.Volume") || "Ses Seviyesi"}</label>
                  <span>{volume}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volume}
                  onChange={(event) => setVolume(Number(event.target.value))}
                  className="add-sound-modal__upload-settings-range"
                  style={{
                    background: `linear-gradient(90deg, #6d5dfc ${volume}%, #e2e8f0 ${volume}%)`,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Floating Notification */}
        {notification && (
          <div
            style={{
              position: "absolute",
              bottom: "24px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "#10b981",
              color: "white",
              padding: "10px 20px",
              borderRadius: "999px",
              fontSize: "13px",
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              animation: "popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards",
            }}
          >
            <LuCheck size={16} />
            {notification}
            <style>
              {`
              @keyframes popIn {
                0% { opacity: 0; transform: translate(-50%, 20px) scale(0.9); }
                100% { opacity: 1; transform: translate(-50%, 0) scale(1); }
              }
            `}
            </style>
          </div>
        )}
      </div>
    );
  }
);

export default LibrarySoundTab;
